import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input, OnInit } from '@angular/core';
import { PopoverController } from '@ionic/angular';

import { INBOX_SORT } from '../../constants';
import { ActionDropdownComponent, ActionDropdownType } from '../action-dropdown/action-dropdown.component';

export interface TableColumn {
  id: string;
  name: string;
  visible: boolean;
  required?: boolean; // Some columns like Status might be required
  minWidth?: number;
  maxWidth?: number;
}

export interface SortOption {
  key: string;
  label: string;
  sortDesc: boolean;
  sortField: string;
}

export interface LayoutOption {
  key: 'table' | 'grid' | 'sidebar';
  label: string;
  icon: string;
}

@Component({
  selector: 'app-inbox-layout-manager',
  templateUrl: './inbox-layout-manager.component.html',
  styleUrls: ['./inbox-layout-manager.component.scss'],
})
export class InboxLayoutManagerComponent implements OnInit {
  @Input() columns: TableColumn[] = [];
  @Input() onSave: (columns: TableColumn[]) => void;
  @Input() currentSort: SortOption;
  @Input() onSortChange: (sort: SortOption) => void;
  @Input() currentLayout: 'table' | 'grid' | 'sidebar';
  @Input() onLayoutChange: (layout: 'table' | 'grid' | 'sidebar') => void;

  workingColumns: TableColumn[] = [];
  sortOptions: SortOption[] = [];
  layoutOptions: LayoutOption[] = [];

  constructor(private popoverCtrl: PopoverController) {}

  ngOnInit() {
    // Create a working copy of the columns
    this.workingColumns = this.columns.map((col) => ({ ...col }));

    // Initialize sort options
    this.sortOptions = [
      {
        key: INBOX_SORT.CREATED_AT_DESCENDING,
        label: 'Newest',
        sortDesc: true,
        sortField: 'createdAt',
      },
      {
        key: INBOX_SORT.CREATED_AT_ASCENDING,
        label: 'Oldest',
        sortDesc: false,
        sortField: 'createdAt',
      },
      {
        key: INBOX_SORT.LAST_ACTIVITY_AT_DESCENDING,
        label: 'Recent message activity',
        sortDesc: true,
        sortField: 'lastActivityAt',
      },
      {
        key: INBOX_SORT.UPDATED_AT_DESCENDING,
        label: 'Recently updated',
        sortDesc: true,
        sortField: 'updatedAt',
      },
      {
        key: INBOX_SORT.PRIORITY_ASCENDING,
        label: 'High priority',
        sortDesc: true,
        sortField: 'priority',
      },
    ];

    // Initialize layout options
    this.layoutOptions = [
      {
        key: 'grid',
        label: 'Kanban',
        icon: 'custom-kanban',
      },
      {
        key: 'table',
        label: 'Table',
        icon: 'list',
      },
      {
        key: 'sidebar',
        label: 'Sidebar',
        icon: 'custom-sidebar',
      },
    ];
  }

  onDrop(event: CdkDragDrop<TableColumn[]>) {
    moveItemInArray(this.workingColumns, event.previousIndex, event.currentIndex);
  }

  toggleColumn(column: TableColumn) {
    if (!column.required) {
      column.visible = !column.visible;
    }
  }

  resetToDefault() {
    // Reset to default visibility and order
    this.workingColumns = this.getDefaultColumns();
  }

  save() {
    if (this.onSave) {
      this.onSave(this.workingColumns);
    }
    this.popoverCtrl.dismiss(this.workingColumns);
  }

  cancel() {
    this.popoverCtrl.dismiss();
  }

  async showSortOptions(event: Event) {
    const options: ActionDropdownType[] = this.sortOptions.map((opt) => ({
      label: opt.label,
      icon: this.currentSort?.key === opt.key ? 'checkmark-outline' : 'lol',
    }));

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();
          const selectedSort = this.sortOptions[idx];

          // Update local state immediately for responsive UI
          this.currentSort = selectedSort;

          // Notify parent component
          if (this.onSortChange) {
            this.onSortChange(selectedSort);
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  onLayoutChangeClick(layout: 'table' | 'grid' | 'sidebar') {
    // Update local state immediately for responsive UI
    this.currentLayout = layout;

    // Notify parent component
    if (this.onLayoutChange) {
      this.onLayoutChange(layout);
    }

    this.popoverCtrl.dismiss();
  }

  getCurrentSortLabel(): string {
    return this.currentSort?.label || 'Sort by';
  }

  private getDefaultColumns(): TableColumn[] {
    return [
      { id: 'status', name: 'Status', visible: true, required: true, minWidth: 84, maxWidth: 84 },
      { id: 'message', name: 'Message', visible: true, required: true, minWidth: 520 },
      { id: 'submitter', name: 'Submitter', visible: true, minWidth: 180 },
      { id: 'customer', name: 'Customer', visible: true, minWidth: 180 },
      { id: 'assignee', name: 'Assignee', visible: true, minWidth: 180 },
      { id: 'ticketType', name: 'Ticket Type', visible: true, minWidth: 180 },
      { id: 'account', name: 'Account', visible: true, minWidth: 180 },
      { id: 'submitted', name: 'Submitted', visible: true, minWidth: 170, maxWidth: 170 },
      { id: 'tags', name: 'Tags', visible: true, minWidth: 250 },
      { id: 'customerTags', name: 'Customer Tags', visible: true, minWidth: 250 },
      { id: 'responseTime', name: 'Response time', visible: false, minWidth: 170, maxWidth: 170 },
      { id: 'resolutionTime', name: 'Resolution time', visible: false, minWidth: 170, maxWidth: 170 },
      { id: 'lastActivityAt', name: 'Last message sent', visible: false, minWidth: 170, maxWidth: 170 },
      { id: 'project', name: 'Project', visible: false, minWidth: 180 },
    ];
  }
}
